import { todayInTimeZone } from "./facilityCalendarDate";

export type WorkOrderDueLabel = {
  text: string;
  /** Past due is an error; other dates and missing due dates are muted. */
  tone: "overdue" | "today" | "upcoming" | "completed" | "none";
};

function formatDate(
  value: string,
  timeZone: string | null | undefined,
  now: Date,
): string | null {
  const date = new Date(value);
  if (Number.isNaN(date.valueOf())) return null;
  const format = (zone: string) => {
    const yearForDate = new Intl.DateTimeFormat("en", {
      timeZone: zone,
      year: "numeric",
    }).format(date);
    const currentYear = new Intl.DateTimeFormat("en", {
      timeZone: zone,
      year: "numeric",
    }).format(now);
    return new Intl.DateTimeFormat("en-US", {
      timeZone: zone,
      month: "short",
      day: "numeric",
      ...(yearForDate === currentYear ? {} : { year: "numeric" }),
    }).format(date);
  };
  try {
    return format(timeZone || "UTC");
  } catch {
    return format("UTC");
  }
}

function dayNumber(dateKey: string): number {
  const [year, month, day] = dateKey.split("-").map(Number);
  return Math.round(Date.UTC(year, month - 1, day) / 86_400_000);
}

/**
 * The list card's due text, relative to the Facility's calendar day. Dates
 * more than one day away use calendar dates; completed Work Orders show when
 * they were completed, and missing due dates are explicit.
 */
export function workOrderDueLabel(
  dueDate: string | null,
  statusCode: string,
  timeZone?: string | null,
  now: Date = new Date(),
  completedOn?: string | null,
): WorkOrderDueLabel {
  if (statusCode === "COMPLETED") {
    const completedDate = completedOn
      ? formatDate(completedOn, timeZone, now)
      : null;
    return {
      text: completedDate ? `Completed ${completedDate}` : "Completed",
      tone: "completed",
    };
  }
  if (statusCode === "CANCELED" || statusCode === "DELETED") {
    return { text: "No due date", tone: "none" };
  }
  if (!dueDate) return { text: "No due date", tone: "none" };
  const due = new Date(dueDate);
  if (Number.isNaN(due.valueOf())) return { text: "No due date", tone: "none" };

  const days =
    dayNumber(todayInTimeZone(timeZone, due)) -
    dayNumber(todayInTimeZone(timeZone, now));

  if (days === 0) return { text: "Due Today", tone: "today" };
  const tone = days < 0 ? "overdue" : "upcoming";
  if (days === -1) return { text: "Due Yesterday", tone };
  if (days === 1) return { text: "Due Tomorrow", tone };
  const dueDateLabel = formatDate(dueDate, timeZone, now);
  return { text: `Due ${dueDateLabel}`, tone };
}
